import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Upload, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { logger } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const projectTypes = [
  { id: 'landing', name: 'Landing Page', description: 'Página de conversão otimizada' },
  { id: 'ecommerce', name: 'E-commerce', description: 'Loja virtual completa' },
  { id: 'sistema', name: 'Sistema Web', description: 'Plataforma ou dashboard personalizado' },
  { id: 'site', name: 'Site Institucional', description: 'Presença digital profissional' },
  { id: 'automacao', name: 'Automação', description: 'WhatsApp, IA, Chatbots' },
  { id: 'outro', name: 'Outro', description: 'Projeto customizado' }
];

const features = [
  'Design Responsivo',
  'SEO Otimizado',
  'Integração com APIs',
  'Painel Admin',
  'Sistema de Login',
  'Pagamento Online',
  'Chatbot IA',
  'Automação WhatsApp',
  'Analytics',
  'Email Marketing'
];

const budgetRanges = [
  { id: '1', label: 'R$ 1.000 - R$ 5.000' },
  { id: '2', label: 'R$ 5.000 - R$ 10.000' },
  { id: '3', label: 'R$ 10.000 - R$ 25.000' },
  { id: '4', label: 'R$ 25.000 - R$ 50.000' },
  { id: '5', label: 'R$ 50.000+' }
];

const deadlines = [
  { id: '1', label: 'Até 1 semana' },
  { id: '2', label: '2-4 semanas' },
  { id: '3', label: '1-2 meses' },
  { id: '4', label: '2-3 meses' },
  { id: '5', label: '3+ meses' }
];

export default function Orcamento() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    project_type: '',
    features: [],
    budget_range: '',
    deadline: '',
    name: '',
    email: '',
    phone: '',
    company: '',
    description: ''
  });

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/quotations`, formData, {
        withCredentials: false,
        headers: { 'Content-Type': 'application/json' },
        timeout: 15000,
      });
      if (res?.data?.id) {
        setStep(6); // Success screen
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (error) {
      logger.error('Error submitting quotation:', error);
      const msg =
        error?.response?.data?.detail ||
        error?.message ||
        'Erro ao enviar orçamento. Tente novamente.';
      alert(typeof msg === 'string' ? msg : 'Erro ao enviar orçamento. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.project_type !== '';
      case 2:
        return formData.features.length > 0;
      case 3:
        return formData.budget_range !== '' && formData.deadline !== '';
      case 4:
        return formData.name && formData.email && formData.phone;
      case 5:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="orcamento-page">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Solicitar <span className="text-[#22D3EE]">Orçamento</span>
          </h1>
          <p className="text-lg text-[#94A3B8]">
            Conte-nos sobre seu projeto em {totalSteps} etapas simples
          </p>
        </motion.div>

        {/* Progress Bar */}
        {step <= totalSteps && (
          <div className="mb-8" data-testid="progress-bar">
            <div className="h-2 bg-[#111827] rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#22D3EE] to-[#4F46E5] rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <div className="flex justify-between mt-2 text-sm text-[#94A3B8]">
              <span>Passo {step} de {totalSteps}</span>
              <span>{Math.round(progress)}%</span>
            </div>
          </div>
        )}

        {/* Form Steps */}
        <div className="bg-[#111827] border border-white/10 rounded-2xl p-8 sm:p-12">
          <AnimatePresence mode="wait">
            {/* Step 1: Project Type */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-project-type"
              >
                <h2 className="text-2xl font-bold mb-6">Que tipo de projeto você precisa?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {projectTypes.map((type) => (
                    <button
                      key={type.id}
                      data-testid={`project-type-${type.id}`}
                      onClick={() => setFormData({ ...formData, project_type: type.id })}
                      className={`p-6 rounded-xl border-2 text-left transition-all ${
                        formData.project_type === type.id
                          ? 'border-[#22D3EE] bg-[#22D3EE]/10'
                          : 'border-white/10 hover:border-[#22D3EE]/50'
                      }`}
                    >
                      <h3 className="text-lg font-semibold mb-2">{type.name}</h3>
                      <p className="text-sm text-[#94A3B8]">{type.description}</p>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 2: Features */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-features"
              >
                <h2 className="text-2xl font-bold mb-6">Quais funcionalidades você precisa?</h2>
                <p className="text-[#94A3B8] mb-6">Selecione todas que se aplicam</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {features.map((feature) => (
                    <button
                      key={feature}
                      data-testid={`feature-${feature.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => {
                        const newFeatures = formData.features.includes(feature)
                          ? formData.features.filter(f => f !== feature)
                          : [...formData.features, feature];
                        setFormData({ ...formData, features: newFeatures });
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all flex items-center ${
                        formData.features.includes(feature)
                          ? 'border-[#22D3EE] bg-[#22D3EE]/10'
                          : 'border-white/10 hover:border-[#22D3EE]/50'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                        formData.features.includes(feature) ? 'bg-[#22D3EE] border-[#22D3EE]' : 'border-white/30'
                      }`}>
                        {formData.features.includes(feature) && <Check size={14} className="text-[#080710]" />}
                      </div>
                      {feature}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 3: Budget & Timeline */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-budget-timeline"
              >
                <h2 className="text-2xl font-bold mb-6">Orçamento e Prazo</h2>
                
                <div className="mb-8">
                  <label className="block text-lg font-semibold mb-4">Qual seu orçamento estimado?</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {budgetRanges.map((range) => (
                      <button
                        key={range.id}
                        data-testid={`budget-${range.id}`}
                        onClick={() => setFormData({ ...formData, budget_range: range.label })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.budget_range === range.label
                            ? 'border-[#22D3EE] bg-[#22D3EE]/10'
                            : 'border-white/10 hover:border-[#22D3EE]/50'
                        }`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-semibold mb-4">Qual o prazo desejado?</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {deadlines.map((deadline) => (
                      <button
                        key={deadline.id}
                        data-testid={`deadline-${deadline.id}`}
                        onClick={() => setFormData({ ...formData, deadline: deadline.label })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.deadline === deadline.label
                            ? 'border-[#22D3EE] bg-[#22D3EE]/10'
                            : 'border-white/10 hover:border-[#22D3EE]/50'
                        }`}
                      >
                        {deadline.label}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Contact Info */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-contact"
              >
                <h2 className="text-2xl font-bold mb-6">Informações de Contato</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Nome Completo *</label>
                    <input
                      type="text"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-[#F8FAFC] focus:border-[#22D3EE] focus:ring-1 focus:ring-[#22D3EE] outline-none"
                      placeholder="Seu nome"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      data-testid="input-email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-[#F8FAFC] focus:border-[#22D3EE] focus:ring-1 focus:ring-[#22D3EE] outline-none"
                      placeholder="seu@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Telefone *</label>
                    <input
                      type="tel"
                      data-testid="input-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-[#F8FAFC] focus:border-[#22D3EE] focus:ring-1 focus:ring-[#22D3EE] outline-none"
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Empresa (opcional)</label>
                    <input
                      type="text"
                      data-testid="input-company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-[#F8FAFC] focus:border-[#22D3EE] focus:ring-1 focus:ring-[#22D3EE] outline-none"
                      placeholder="Nome da empresa"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 5: Additional Details */}
            {step === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-description"
              >
                <h2 className="text-2xl font-bold mb-6">Detalhes Adicionais</h2>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Conte-nos mais sobre seu projeto (opcional)
                  </label>
                  <textarea
                    data-testid="input-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={6}
                    className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-[#F8FAFC] focus:border-[#22D3EE] focus:ring-1 focus:ring-[#22D3EE] outline-none resize-none"
                    placeholder="Descreva sua visão, objetivos, referências ou qualquer informação relevante..."
                  />
                </div>
              </motion.div>
            )}

            {/* Success Screen */}
            {step === 6 && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
                data-testid="success-screen"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-[#22D3EE] to-[#4F46E5] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check size={40} className="text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-4">Orçamento Enviado!</h2>
                <p className="text-lg text-[#94A3B8] mb-8">
                  Recebemos seu pedido de orçamento. Nossa equipe entrará em contato em breve.
                </p>
                <button
                  data-testid="back-home-button"
                  onClick={() => navigate('/')}
                  className="bg-[#22D3EE] text-[#080710] px-8 py-3 rounded-lg font-semibold hover:bg-[#22D3EE]/90 hover:shadow-[0_0_20px_rgba(34,211,238,0.35)] transition-all"
                >
                  Voltar ao Início
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          {step <= totalSteps && (
            <div className="flex justify-between mt-8 pt-8 border-t border-white/10">
              <button
                data-testid="back-button"
                onClick={handleBack}
                disabled={step === 1}
                className="flex items-center space-x-2 text-[#94A3B8] hover:text-[#F8FAFC] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Voltar</span>
              </button>

              {step < totalSteps ? (
                <button
                  data-testid="next-button"
                  onClick={handleNext}
                  disabled={!isStepValid()}
                  className="flex items-center space-x-2 bg-[#22D3EE] text-[#080710] px-6 py-3 rounded-lg font-semibold hover:bg-[#22D3EE]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <span>Próximo</span>
                  <ArrowRight size={20} />
                </button>
              ) : (
                <button
                  data-testid="submit-button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex items-center space-x-2 bg-[#22D3EE] text-[#080710] px-6 py-3 rounded-lg font-semibold hover:bg-[#22D3EE]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <span>Enviar Orçamento</span>
                      <Check size={20} />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
